// =============================================================================
// VulnTracker — Developer / API Reference Page
// =============================================================================
import {
  CodeBracketIcon,
  KeyIcon,
  CommandLineIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

// ── Data ─────────────────────────────────────────────────────────────────────

const apiGroups = [
  {
    group: 'Authentication',
    endpoints: '/auth/register, /auth/login, /auth/refresh, /auth/me, /auth/change-password',
  },
  {
    group: 'Organisations',
    endpoints: 'CRUD for organisations; list projects; member management',
  },
  {
    group: 'Projects',
    endpoints: 'CRUD for projects; associate with org; list components',
  },
  {
    group: 'Components',
    endpoints: 'CRUD for components; trigger on-demand scan; view match history',
  },
  {
    group: 'SBOM Import',
    endpoints: 'Upload CycloneDX / SPDX / lock-file; bulk component extraction',
  },
  {
    group: 'Vulnerabilities',
    endpoints: 'Search CVE database; filter by severity / ecosystem; detail view',
  },
  {
    group: 'Vulnerability Matches',
    endpoints: 'List active matches; update remediation status; bulk dismiss',
  },
  {
    group: 'Notifications',
    endpoints: 'Configure rules; list delivery history; test a channel',
  },
  {
    group: 'Dashboard',
    endpoints: 'Aggregated stats, severity counts, top-affected projects, trends',
  },
  {
    group: 'Users',
    badge: 'Admin',
    endpoints: 'List users; change role; deactivate; API key management',
  },
  {
    group: 'Audit Log',
    badge: 'Admin',
    endpoints: 'Query audit events; export; verify HMAC integrity',
  },
];

const pipelineSteps = [
  'POST a new SBOM at every build to keep the component inventory current.',
  'Query the API for any Critical / High vulnerabilities before releasing to production.',
  'Fail the build automatically if unacknowledged critical CVEs exist.',
  'Receive Jira tickets automatically for every newly detected vulnerability.',
];

// ── Component ─────────────────────────────────────────────────────────────────

export function DeveloperPage() {
  return (
    <div className="space-y-10 max-w-5xl">

      {/* ── Page header ── */}
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-primary-100 flex items-center justify-center">
          <CodeBracketIcon className="h-7 w-7 text-primary-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">REST API &amp; Integrations</h1>
          <p className="mt-1 text-sm text-gray-500 max-w-2xl">
            Every VulnTracker capability is available programmatically through a versioned,
            authenticated REST API — designed for seamless integration with your existing
            DevSecOps toolchain.
          </p>
        </div>
      </div>

      {/* ── Section 6.1 ── */}
      <section className="card space-y-6">
        {/* Section title */}
        <div className="flex items-center gap-3">
          <span className="flex-shrink-0 h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center">
            <DocumentTextIcon className="h-5 w-5 text-blue-600" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 leading-tight">Fully Documented REST API</h2>
          </div>
        </div>

        <p className="text-sm text-gray-600 leading-relaxed">
          Every VulnTracker function is exposed through a versioned, authenticated REST API with
          automatic <span className="font-medium text-gray-800">OpenAPI 3.0 / Swagger</span> documentation
          generated at runtime. Integration teams can explore and test every endpoint interactively
          without any additional tooling.
        </p>

        {/* API endpoints table */}
        <div className="overflow-hidden rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 w-48">
                  API Group
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Key Endpoints &amp; Capabilities
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {apiGroups.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {row.group}
                      {row.badge && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700">
                          {row.badge}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs leading-relaxed">
                    {row.endpoints}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Section 6.2 ── */}
      <section className="card space-y-6">
        {/* Section title */}
        <div className="flex items-center gap-3">
          <span className="flex-shrink-0 h-8 w-8 rounded-lg bg-green-50 flex items-center justify-center">
            <CommandLineIcon className="h-5 w-5 text-green-600" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 leading-tight">CI/CD Pipeline Integration</h2>
          </div>
        </div>

        <p className="text-sm text-gray-600 leading-relaxed">
          VulnTracker ships with API key authentication scoped to specific permissions, making it
          straightforward to embed vulnerability checks directly into your build pipelines — without
          exposing broader system access.
        </p>

        {/* Pipeline steps */}
        <ul className="space-y-3">
          {pipelineSteps.map((step, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
              <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>{step}</span>
            </li>
          ))}
        </ul>

        {/* API key security callout */}
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-4">
          <div className="flex-shrink-0">
            <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center">
              <KeyIcon className="h-5 w-5 text-amber-600" />
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-amber-900">API Key Security</p>
            <p className="text-sm text-amber-800 leading-relaxed">
              API keys are prefixed <code className="px-1 py-0.5 rounded bg-amber-100 font-mono text-xs font-bold">vt_</code> for
              easy identification in secret scanners and are stored as{' '}
              <span className="font-medium">SHA-256 hashes</span> — never in plain text.
            </p>
          </div>
        </div>

        {/* Quick-start code snippet */}
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">Quick-start example</p>
          <div className="rounded-xl bg-gray-900 p-4 overflow-x-auto">
            <pre className="text-xs leading-relaxed text-gray-300 font-mono whitespace-pre">{`# 1. Upload SBOM at build time
curl -X POST https://app.securusmind.com/api/v1/sbom/upload \\
  -H "Authorization: Bearer vt_••••••••" \\
  -F "file=@sbom.cdx.json"

# 2. Gate on critical CVEs before deploying
curl https://app.securusmind.com/api/v1/matches?severity=critical&status=open \\
  -H "Authorization: Bearer vt_••••••••" | \\
  jq 'if .total > 0 then error("Unresolved critical CVEs") else . end'`}
            </pre>
          </div>
        </div>

        {/* Security note */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 flex gap-3 items-start">
          <LockClosedIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-gray-500 leading-relaxed">
            All API traffic is encrypted in transit (TLS 1.2+). Rate limits apply per API key.
            Rotate keys regularly from the <span className="font-medium text-gray-700">Settings → API Keys</span> panel.
            Audit log entries are created for every key issuance and revocation event.
          </p>
        </div>
      </section>
    </div>
  );
}
